/* tslint:disable:no-bitwise */
// noinspection PointlessArithmeticExpressionJS
import { DataTexture, Float32BufferAttribute, FloatType, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, RGBAFormat, } from "three";
import { HarfBuzz } from "../../external/HarfBuzz";
import { LabelCollectionMaterial } from "../../materials";
import { PreparingObject } from "../preparing_object";
/**
 * A collection of text labels pinned to a 3D position.
 *
 * Manages generating geometry, caching glyphs and shaping the text labels. All labels within a collection are rendered
 * using as few draw calls as possible.
 *
 * The API is designed so that the number of draw calls emitted by a `LabelCollection` is equal to the number of unique
 * glyph atlas textures referenced by the collection. In the common, and ideal case, this will be a single draw call.
 *
 * This primitive exists for minimizing the per-frame CPU cost of rendering text labels to enable drawing 10s of
 * thousands of labels without choking on the number of draw calls.
 *
 *
 * LabelCollection has restrictions on the features it supports to enable the small number of draw calls.
 *   - All labels must share the same shader and THREE.Material instance.
 *   - Culling can not be performed at a fine granularity. If one label is on screen they will all be feed into the
 *     pipeline. This means a lot of pressure will be placed on vertex shader units.
 *   - All labels effectively form one object. If any label changes then the data for the whole collection needs to be
 *     rebuilt. Frequently changing labels should *not* be mixed in the same collection as labels that never change.
 *   - Per-glyph data is sent via the input assembler (as vertex or instance attributes). Complex data types need to be
 *     crunched and reconstructed in the shader.
 *
 *
 * These downsides are intentional trade-offs in pursuit of minimizing CPU overhead from draw calls.
 */
export class LabelCollection extends PreparingObject {
    /**
     * Constructs a new LabelCollection. A GlyphCache is required as it will be used to source glyph textures.
     * @param glyphCache The glyph cache for the font all labels in the collection will share.
     */
    constructor(glyphCache) {
        super();
        /**
         * The list of text labels that belong to the collection
         */
        this.labels = [];
        /**
         * Flag that invalidates the existing cache of text data and forces it to be regenerated the next time the
         * LabelCollection is rendered.
         *
         * This must be set to `true` after any changes to `this.labels` or its contents are made. Any changes made will
         * *not* be visible until after this flag is set to true.
         */
        this.needsUpdate = true;
        /**
         * Array of mesh objects that correlate to the glyph cache textures
         * @private
         */
        this.meshes = [];
        this._opacity = 1;
        /**
         * Texture for storing label transforms for access on the GPU
         * @private
         */
        this.transformAtlas = {
            size: 2048,
            texture: null,
        };
        this.frustumCulled = false;
        this.glyphCache = glyphCache;
    }
    /**
     * Returns the maximum number of labels that the collection can hold.
     *
     * This upper limit is constrained by WebGL and GPU support.
     */
    get maxLabelNum() {
        return 2048 * 2048;
    }
    /**
     * Force a build of all the text rendering data, if an update is required.
     *
     * Useful for eating the build cost upfront rather than lazily on first render.
     */
    prepare() {
        if (this.needsUpdate) {
            this.rebuildImpl();
        }
    }
    get opacity() {
        return this._opacity;
    }
    set opacity(v) {
        this._opacity = v;
        for (const mesh of this.meshes) {
            mesh.material.opacity = v;
            mesh.material.needsUpdate = true;
        }
    }
    rebuildImpl() {
        // Calculate the size the transform atlas needs to be for the current set of labels.
        this.transformAtlas.size = this.selectTransformAtlasSize();
        // Data that will back the transform atlas texture
        const transformAtlasData = new Float32Array(this.transformAtlas.size * this.transformAtlas.size * 4);
        const perAtlasGeometry = [];
        for (let labelI = 0; labelI < this.labels.length; labelI += 1) {
            const label = this.labels[labelI];
            // Write the local space position into the transform atlas
            transformAtlasData[labelI * 4 + 0] = label.position.x;
            transformAtlasData[labelI * 4 + 1] = label.position.y;
            transformAtlasData[labelI * 4 + 2] = label.position.z;
            // Shape the label's text using harfbuzz
            const shaped = this.shapeLabelText(label);
            // Generate glyph instance data based on the string of glyphs emitted by the shaping operation
            this.generateGlyphInstanceData(labelI, shaped, perAtlasGeometry);
        }
        // Upload the new transform atlas. Dispose the old texture if one already exists
        if (this.transformAtlas.texture !== null) {
            this.transformAtlas.texture.dispose();
        }
        this.transformAtlas.texture = new DataTexture(transformAtlasData, this.transformAtlas.size, this.transformAtlas.size, RGBAFormat, FloatType);
        this.transformAtlas.texture.needsUpdate = true;
        // Clear and dispose the old meshes, if they exist
        if (this.meshes.length > 0) {
            this.clear();
            for (const old of this.meshes) {
                old.material.dispose();
                old.geometry.dispose();
            }
            this.meshes.length = 0;
        }
        // Construct one mesh for each glyph cache texture, as we need one draw call per texture
        for (let i = 0; i < this.glyphCache.textureCount; i += 1) {
            const geometry = new InstancedBufferGeometry();
            const atlasGeometry = perAtlasGeometry[i];
            geometry.instanceCount = atlasGeometry.numGlyphs;
            geometry.setAttribute("position", new Float32BufferAttribute(LabelCollection.PLANE_NUMS, 3, false));
            geometry.setIndex([0, 1, 2, 0, 2, 3]);
            geometry.setAttribute("skGlyphBoundingBox", new InstancedBufferAttribute(new Float32Array(atlasGeometry.glyphBoundingBoxes), 4, false));
            geometry.setAttribute("skGlyphTexCoords", new InstancedBufferAttribute(new Float32Array(atlasGeometry.glyphTexCoords), 4, false));
            geometry.setAttribute("skGlyphLabelIndex", new InstancedBufferAttribute(new Float32Array(atlasGeometry.glyphLabelIndices), 1, false));
            const mesh = new Mesh(geometry, new LabelCollectionMaterial({
                map: this.glyphCache.getTexture(i),
                transformAtlas: this.transformAtlas.texture,
                transparent: true,
                depthWrite: false,
                depthTest: false,
                strokeWidth: 40,
                glyphScale: 0.00002,
                opacity: this._opacity,
            }));
            mesh.frustumCulled = false;
            this.meshes.push(mesh);
            this.add(mesh);
        }
        this.needsUpdate = false;
    }
    /**
     * Internal function for selecting a transform atlas texture size based on the number of labels in the collection.
     *
     * We pick the smallest possible atlas size that fits all the labels to avoid allocating a 2048*2048 texture for
     * 100 labels. A 2048*2048 R32G32B32_SFLOAT texture consumes ~50MB of memory, so we should only eat the cost if we
     * have to.
     * @private
     */
    selectTransformAtlasSize() {
        // Declare a fixed set of size candidates
        //
        // We set an upper limit at 2048 * 2048 as 4 million labels is probably enough for anyone
        const candidates = [
            [2 * 2, 2],
            [4 * 4, 4],
            [8 * 8, 8],
            [16 * 16, 16],
            [32 * 32, 32],
            [64 * 64, 64],
            [128 * 128, 128],
            [256 * 256, 256],
            [512 * 512, 512],
            [1024 * 1024, 1024],
            [2048 * 2048, 2048],
        ];
        // Iterate over the candidates and select the smallest texture size that will fit the number of labels we're
        // trying to store. There may be some waste.
        for (const [maxLabels, size] of candidates) {
            if (this.labels.length <= maxLabels) {
                return size;
            }
        }
        // If we've gotten here then we have too many labels for any of our candidate sizes. In this case we've exceeded
        // the label count limit, which is a hard error, so we throw.
        throw new Error("Exceeded maximum number of labels in a single LabelCollection");
    }
    shapeLabelText(label) {
        // Create and fill the buffer
        const buffer = HarfBuzz.module.createBuffer();
        buffer.addText(label.text);
        buffer.guessSegmentProperties();
        // Shape the text and get our results
        HarfBuzz.module.shape(this.glyphCache.font, buffer);
        const json = buffer.json();
        // Free the buffer memory
        buffer.destroy();
        return json;
    }
    generateGlyphInstanceData(labelI, shaped, perAtlasGeometry) {
        let cursorX = 0;
        let cursorY = 0;
        for (const shape of shaped) {
            // Add (and potentially render) the glyph into the cache
            const glyph = this.glyphCache.addGlyph(shape.g);
            // The glyph could be in a newly created texture we don't have a slot in the 'perAtlasGeometry' array for
            // yet. Here we check if we need to create any new 'perAtlasGeometry' entries, and create them.
            if (glyph.atlasIndex >= perAtlasGeometry.length) {
                for (let i = glyph.atlasIndex; i < this.glyphCache.textureCount; i += 1) {
                    perAtlasGeometry.push({
                        numGlyphs: 0,
                        glyphBoundingBoxes: [],
                        glyphTexCoords: [],
                        glyphLabelIndices: [],
                    });
                }
            }
            // Now we know our entry exists we can fetch it and write our data into it
            const atlasGeometry = perAtlasGeometry[glyph.atlasIndex];
            // We need to know how many glyphs each geometry will contain. This becomes our instance number.
            atlasGeometry.numGlyphs += 1;
            // Calculate the offset position to render the glyph from
            const glyphWidth = glyph.sdfViewBox[2] - glyph.sdfViewBox[0];
            const glyphHeight = glyph.sdfViewBox[3] - glyph.sdfViewBox[1];
            const offsetX = cursorX + shape.dx + glyph.sdfViewBox[0];
            const offsetY = cursorY + shape.dy + glyph.sdfViewBox[1];
            // Calculate a 2D bounding box for the glyph
            atlasGeometry.glyphBoundingBoxes.push(offsetX);
            atlasGeometry.glyphBoundingBoxes.push(offsetY);
            atlasGeometry.glyphBoundingBoxes.push(glyphWidth);
            atlasGeometry.glyphBoundingBoxes.push(glyphHeight);
            // Push the bounding box for the glyph's region inside the font atlas texture (normalized)
            atlasGeometry.glyphTexCoords.push(glyph.x / this.glyphCache.size);
            atlasGeometry.glyphTexCoords.push(glyph.y / this.glyphCache.size);
            atlasGeometry.glyphTexCoords.push(glyph.w / this.glyphCache.size);
            atlasGeometry.glyphTexCoords.push(glyph.h / this.glyphCache.size);
            // Push the index of the label that the instance belongs to. This is used to lookup the label transform
            // in the transform atlas texture.
            atlasGeometry.glyphLabelIndices.push(labelI);
            // Advance the cursor by the amount requested by the shape
            cursorX += shape.ax;
            cursorY += shape.ay;
        }
    }
}
LabelCollection.PLANE_NUMS = [0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 1, 0];
